import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Models from './pages/Models';
import ModelDetail from './pages/ModelDetail';
import Productions from './pages/Productions';
import ProductionDetail from './pages/ProductionDetail';
import Documents from './pages/Documents';
import DocumentNew from './pages/DocumentNew';
import DocumentDetail from './pages/DocumentDetail';
import Compliance from './pages/Compliance';
import Users from './pages/Users';
import Settings from './pages/Settings';
import AdminTenants from './pages/admin/Tenants';
import AdminBackups from './pages/admin/Backups';
import SuperAdminConsole from './pages/SuperAdminConsole';
import StudioLogin from './pages/StudioLogin';
import StudioRegister from './pages/StudioRegister';
import ModelNew from './pages/ModelNew';
import ProductionNew from './pages/ProductionNew';
import MyProfile from './pages/MyProfile';
import ModelGuide from './pages/guides/ModelGuide';
import StudioGuide from './pages/guides/StudioGuide';
import SuperAdminGuide from './pages/guides/SuperAdminGuide';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  if (user?.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/studiologin" element={<StudioLogin />} />
      <Route path="/studioregister" element={<StudioRegister />} />

      {/* Super Admin Console (protected by Cloudflare Access) */}
      <Route path="/superadminconsole" element={<SuperAdminConsole />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="my-profile" element={<MyProfile />} />
        <Route path="models" element={<Models />} />
        <Route path="models/new" element={<ModelNew />} />
        <Route path="models/:id" element={<ModelDetail />} />
        <Route path="productions" element={<Productions />} />
        <Route path="productions/new" element={<ProductionNew />} />
        <Route path="productions/:id" element={<ProductionDetail />} />
        <Route path="documents" element={<Documents />} />
        <Route path="documents/new" element={<DocumentNew />} />
        <Route path="documents/:id" element={<DocumentDetail />} />
        <Route path="compliance" element={<Compliance />} />
        <Route path="users" element={<Users />} />
        <Route path="settings" element={<Settings />} />

        {/* Guide routes */}
        <Route path="guide/model" element={<ModelGuide />} />
        <Route path="guide/studio" element={<StudioGuide />} />
        <Route path="guide/super-admin" element={<SuperAdminGuide />} />

        {/* Admin routes */}
        <Route
          path="admin/tenants"
          element={
            <AdminRoute>
              <AdminTenants />
            </AdminRoute>
          }
        />
        <Route
          path="admin/backups"
          element={
            <AdminRoute>
              <AdminBackups />
            </AdminRoute>
          }
        />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
